import { useEffect, useState } from "react";
import Logo from "./Logo";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import { lookupProperty, track } from "../lib/api";
import { DEMO_ADDRESS } from "../lib/demoJourney";

// Intake — single address. Submits to /api/property with `includeOwner: true`
// so the owner-of-record block is available for the soft-verification step
// that follows in SellerApp. Per the "seamless" principle, the only input
// the seller takes before AI starts working on their behalf.
export default function Intake({ onStart, onHome, isDemo }) {
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [manualMode, setManualMode] = useState(false);
  const [manual, setManual] = useState({ beds: "", baths: "", sqft: "", year_built: "" });
  const [coords, setCoords] = useState(null);

  const setM = (k, v) => setManual((m) => ({ ...m, [k]: v }));

  // Demo mode: 5s intro pause, then auto-type the address + fake lookup.
  useEffect(() => {
    if (!isDemo) return;
    let i = 0;
    let interval;
    const delay = setTimeout(() => {
      interval = setInterval(() => {
        i++;
        setAddress(DEMO_ADDRESS.slice(0, i));
        if (i >= DEMO_ADDRESS.length) {
          clearInterval(interval);
          setTimeout(() => {
            setLoading(true);
            setTimeout(() => onStart(DEMO_ADDRESS, null), 1800);
          }, 700);
        }
      }, 52);
    }, 5000);
    return () => { clearTimeout(delay); clearInterval(interval); };
  }, []); // eslint-disable-line

  const submitManual = (e) => {
    e?.preventDefault();
    const a = address.trim();
    if (a.length < 8) { setErr("Enter a full address first."); return; }
    // Build a minimal property shape so downstream stages have something to read.
    const property = {
      property: [{
        building: {
          rooms: { beds: Number(manual.beds) || null, bathstotal: Number(manual.baths) || null },
          size: { bldgsize: Number(manual.sqft) || null },
          yearbuilt: Number(manual.year_built) || null,
        },
        lot: {},
      }],
    };
    track("seller_intake_manual", { address: a });
    onStart(a, property, coords);
  };

  const submit = async (e) => {
    e?.preventDefault();
    const a = address.trim();
    if (a.length < 8) { setErr("Enter a full address — street, city, state."); return; }
    setLoading(true); setErr("");
    track("seller_intake_submit", { address: a });
    const data = await lookupProperty(a, { includeOwner: true });
    if (!data) {
      track("seller_intake_not_found", { address: a });
      setLoading(false);
      setManualMode(true);
      return;
    }
    onStart(a, data, coords);
  };

  return (
    <div className="s-app">
      <header className="s-top">
        <div className="s-brand" onClick={onHome}>
          <Logo />
          <div className="s-brand-name">6zero.ai</div>
        </div>
        <button type="button" className="s-back ml-auto" onClick={onHome}>← back</button>
      </header>

      <div className="s-intake">
        <div className="s-intake-blob1" />
        <div className="s-intake-blob2" />
        <div className="s-intake-card">
          <div className="s-intake-pill">
            <span className="s-intake-dot" />
            <span>Sell on 6zero.ai · Private beta</span>
          </div>
          <h1>Just give us your address.<br /><em>We handle the rest.</em></h1>
          <p className="lead">
            Valuation, prep, disclosures, staging, photos, listing, MLS, showings, offers,
            escrow, closing — all run for you. Keep the 3% you'd give a listing agent.
          </p>
          <form onSubmit={submit} className="s-intake-form">
            {isDemo ? (
              <input
                value={address}
                readOnly
                className="s-intake-input"
                placeholder="123 Main St, San Francisco, CA"
                style={{ width: "100%", pointerEvents: "none" }}
              />
            ) : (
              <AddressAutocomplete
                value={address}
                onChange={setAddress}
                onPick={(f) => {
                  setAddress(f.place_name);
                  setCoords(Array.isArray(f.center) ? { lng: f.center[0], lat: f.center[1] } : null);
                }}
                types="address"
                placeholder="123 Main St, San Francisco, CA"
                className="s-aac-wrap"
                inputClassName="s-intake-input"
                autoFocus
                disabled={loading}
              />
            )}
            <button
              type="submit"
              className="s-intake-btn"
              disabled={loading || address.trim().length < 8}
            >
              {loading ? "Looking up…" : "Start →"}
            </button>
          </form>
          {err && <div className="s-intake-err">{err}</div>}

          {manualMode && (
            <div style={{ marginTop: 24, background: "#f7faf8", border: "1px solid #d8ede6", borderRadius: 14, padding: "20px 22px", textAlign: "left" }}>
              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: "#c47c1a", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>
                Couldn't auto-pull property data
              </div>
              <p style={{ fontSize: 13, color: "#5b7365", lineHeight: 1.6, marginBottom: 16 }}>
                Enter your property details manually and we'll continue from there.
              </p>
              <form onSubmit={submitManual}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
                  {[
                    { label: "Bedrooms", key: "beds", placeholder: "3" },
                    { label: "Bathrooms", key: "baths", placeholder: "2" },
                    { label: "Sqft", key: "sqft", placeholder: "1640" },
                    { label: "Year built", key: "year_built", placeholder: "1962" },
                  ].map(({ label, key, placeholder }) => (
                    <label key={key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <span style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", color: "#5b7365", letterSpacing: "0.06em", textTransform: "uppercase" }}>{label}</span>
                      <input
                        type="number"
                        value={manual[key]}
                        onChange={(e) => setM(key, e.target.value)}
                        placeholder={placeholder}
                        style={{ background: "#fff", border: "1px solid #d8ede6", borderRadius: 8, padding: "9px 12px", fontSize: 14, color: "#1c2b25", fontFamily: "'Outfit',sans-serif" }}
                      />
                    </label>
                  ))}
                </div>
                <button type="submit" className="s-intake-btn" style={{ width: "100%", marginTop: 4 }}>
                  Continue →
                </button>
              </form>
            </div>
          )}

          <div className="s-intake-stats">
            <div>
              <div className="s-intake-stat-v">End-to-end</div>
              <div className="s-intake-stat-l">home sale, handled</div>
            </div>
            <div>
              <div className="s-intake-stat-v">$0</div>
              <div className="s-intake-stat-l">listing-agent fee</div>
            </div>
            <div>
              <div className="s-intake-stat-v">~3%</div>
              <div className="s-intake-stat-l">avg seller saves</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
